"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface CountUpProps {
  end: number;
  duration?: number;
  isVisible: boolean;
  isFloat?: boolean;
  suffix?: string;
}
function CountUp({
  end,
  duration = 2500,
  isVisible,
  isFloat = false,
  suffix = ""
}: CountUpProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(easeProgress * end);
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [end, duration, isVisible]);
  const displayValue = isFloat ? count.toFixed(1) : Math.floor(count).toLocaleString();
  return <span>
      {displayValue}
      {suffix}
    </span>;
}
const statsData = [{
  label: "4 — Affiliated Companies",
  value: 4,
  suffix: "",
  isFloat: false
}, {
  label: "3 — USA, Kenya & Guinea Presence",
  value: 3,
  suffix: "",
  isFloat: false
}, {
  label: "Multi-Sector — Trade, Supply & Logistics",
  value: 1,
  suffix: "",
  isFloat: false
}, {
  label: "Cross-Border — Market Access",
  value: 1,
  suffix: "",
  isFloat: false
}];
export default function MetroRideInNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224772" id="our-business-platform" className="relative w-full bg-gradient-to-b from-secondary to-zinc-950 py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-10 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/global_logistics_grid.png)] bg-cover bg-center bg-no-repeat mix-blend-overlay pointer-events-none" />

      {}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(10,15,30,0.9)_100%)] pointer-events-none" />

      <div ref={ref} className="relative z-10 container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-8">
          {statsData.map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-primary" style={{
            textShadow: "0 4px 20px rgba(218, 165, 32, 0.3)"
          }}>
                {i === 2 || i === 3 ? <span className="text-3xl md:text-4xl lg:text-5xl leading-tight">
                    {stat.label.split(" — ")[0]}
                  </span> : <CountUp end={stat.value} isVisible={isVisible} isFloat={stat.isFloat} suffix={stat.suffix} />}
              </div>
              <div className="w-12 h-0.5 bg-primary/40 rounded-md mb-2" />
              <h3 className="text-sm md:text-base font-mono font-medium text-zinc-400 uppercase tracking-widest">
                {i === 2 || i === 3 ? stat.label.split(" — ")[1] : stat.label}
              </h3>
            </div>)}
        </div>
      </div>
    </section>;
}